import {
	getCheckboxDefinition,
	getSelectDefinition,
	useAutocompleteDefinition,
	useDateDefinition,
	useFileUploadDefinition,
	useInputDefinition,
} from '@gov-nx/utils/common';
import { usePPPLocale } from '../../usePPPLocale';
import { type FormDefinition2, useFormBuilder } from '@gov-nx/core/service';
import type { FormStep2Fields, FormStep2FormData } from './formSteps.types';
import { usePPPTypesQuery } from '../hooks/usePPPTypesQuery';
import { PPPTypeOptions } from '../constants';
import { useDateRangeFormDefinition } from '@gov-nx/core/hooks';

export const useFormStep2 = (
	isDisabled: boolean
): FormDefinition2<FormStep2FormData, FormStep2Fields> => {
	const { t } = usePPPLocale();
	const { options } = usePPPTypesQuery();

	const PPPSelect = getSelectDefinition({
		name: 'PPPSelect',
		label: t('zapis.form.step2.PPPSelect.label'),
		isRequired: true,
		isRequiredMessage: t('zapis.form.step2.PPPSelect.required'),
		options: [
			{
				label: t('zapis.form.step2.PPPSelect.placeholder'),
				value: PPPTypeOptions.empty,
			},
			{
				label: t('zapis.form.step2.PPPSelect.podnet-k-doplneni-seznamu'),
				value: PPPTypeOptions.PodnetKDoplneniSeznamu,
			},
			...options,
		],
		isDisabled,
	});

	const missingPPP = useInputDefinition({
		name: 'missingPPP',
		label: t('zapis.form.step2.missingPPP.label'),
		placeholder: t('zapis.form.step2.missingPPP.placeholder'),
		isDisabled,
		isRequiredWhen: {
			fieldName: 'PPPSelect',
			is: PPPTypeOptions.PodnetKDoplneniSeznamu,
			errorMessage: t('zapis.form.step2.missingPPP.error-message'),
		},
	});

	const missingPPPOvm = useAutocompleteDefinition({
		name: 'missingPPPOvm',
		label: t('zapis.form.step2.missingPPPOvm.label'),
		placeholder: t('zapis.form.step2.missingPPPOvm.placeholder'),
		isDisabled,
		isRequiredWhen: {
			fieldName: 'PPPSelect',
			is: PPPTypeOptions.PodnetKDoplneniSeznamu,
			errorMessage: t('zapis.form.step2.missingPPPOvm.error-message'),
		},
	});

	const reasonForAddingMissingPPP = useInputDefinition({
		name: 'reasonForAddingMissingPPP',
		label: t('zapis.form.step2.reasonForAddingMissingPPP.label'),
		placeholder: t('zapis.form.step2.reasonForAddingMissingPPP.placeholder'),
		isDisabled,
		isRequiredWhen: {
			fieldName: 'PPPSelect',
			is: PPPTypeOptions.PodnetKDoplneniSeznamu,
			errorMessage: t(
				'zapis.form.step2.reasonForAddingMissingPPP.error-message'
			),
		},
	});

	const pppOvmAutocomplete = useAutocompleteDefinition({
		name: 'pppOvmAutocomplete',
		isRequiredWhenAll: {
			conditions: [
				{ fieldName: 'isOVMDefunct', is: false },
				{
					fieldName: 'PPPSelect',
					isNot: PPPTypeOptions.PodnetKDoplneniSeznamu,
				},
			],
			errorMessage: t('zapis.form.step2.pppOvmAutocomplete.required'),
		},
		label: t('zapis.form.step2.pppOvmAutocomplete.label'),
		placeholder: t('zapis.form.step2.pppOvmAutocomplete.placeholder'),
		isDisabled,
	});

	const isOVMDefunct = getCheckboxDefinition({
		name: 'isOVMDefunct',
		label: t('zapis.form.step2.isOVMDefunct.label'),
		isDisabled,
	});

	const defunctOVMName = useInputDefinition({
		name: 'defunctOVMName',
		label: t('zapis.form.step2.defunctOVMName.label'),
		placeholder: t('zapis.form.step2.defunctOVMName.placeholder'),
		isRequiredWhen: {
			fieldName: 'isOVMDefunct',
			is: true,
			errorMessage: t('zapis.form.step2.defunctOVMName.error-message'),
		},
		isDisabled,
	});

	const defunctOVMStreet = useInputDefinition({
		name: 'defunctOVMStreet',
		label: t('zapis.form.step2.defunctOVMStreet.label'),
		placeholder: t('zapis.form.step2.defunctOVMStreet.placeholder'),
		isDisabled,
	});

	const defunctOVMCity = useInputDefinition({
		name: 'defunctOVMCity',
		label: t('zapis.form.step2.defunctOVMCity.label'),
		placeholder: t('zapis.form.step2.defunctOVMCity.placeholder'),
		isRequiredWhen: {
			fieldName: 'isOVMDefunct',
			is: true,
			errorMessage: t('zapis.form.step2.defunctOVMCity.error-message'),
		},
		isDisabled,
	});

	const defunctOVMPostalCode = useInputDefinition({
		name: 'defunctOVMPostalCode',
		label: t('zapis.form.step2.defunctOVMPostalCode.label'),
		placeholder: t('zapis.form.step2.defunctOVMPostalCode.placeholder'),
		isRequiredWhen: {
			fieldName: 'isOVMDefunct',
			is: true,
			errorMessage: t('zapis.form.step2.defunctOVMPostalCode.error-message'),
		},
		isDisabled,
	});

	const defunctOVMCityPart = useInputDefinition({
		name: 'defunctOVMCityPart',
		label: t('zapis.form.step2.defunctOVMCityPart.label'),
		placeholder: t('zapis.form.step2.defunctOVMCityPart.placeholder'),
		isDisabled,
	});

	const defunctOVMDescriptiveNumber = useInputDefinition({
		name: 'defunctOVMDescriptiveNumber',
		label: t('zapis.form.step2.defunctOVMDescriptiveNumber.label'),
		placeholder: t('zapis.form.step2.defunctOVMDescriptiveNumber.placeholder'),
		streetNumbers: {
			isDescriptiveNumber: {
				validateWhen: { fieldName: 'isOVMDefunct', is: true },
			},
		},
		isDisabled,
	});

	const defunctOVMEvidenceNumber = useInputDefinition({
		name: 'defunctOVMEvidenceNumber',
		label: t('zapis.form.step2.defunctOVMEvidenceNumber.label'),
		placeholder: t('zapis.form.step2.defunctOVMEvidenceNumber.placeholder'),
		streetNumbers: {
			isEvidenceNumber: {
				validateWhen: { fieldName: 'isOVMDefunct', is: true },
			},
		},
		isDisabled,
	});

	const defunctOVMOrientationNumber = useInputDefinition({
		name: 'defunctOVMOrientationNumber',
		label: t('zapis.form.step2.defunctOVMOrientationNumber.label'),
		placeholder: t('zapis.form.step2.defunctOVMOrientationNumber.placeholder'),
		streetNumbers: {
			isOrientationNumber: {
				validateWhen: { fieldName: 'isOVMDefunct', is: true },
			},
		},
		isDisabled,
	});

	const number = useInputDefinition({
		name: 'number',
		label: t('zapis.form.step2.number.label'),
		placeholder: t('zapis.form.step2.number.placeholder'),
		isRequired: true,
		isDisabled,
	});

	const issueDate = useDateDefinition({
		name: 'issueDate',
		label: t('zapis.form.step2.issueDate.label'),
		isRequired: true,
		isDisabled,
	});

	const [datumOd, datumDo] = useDateRangeFormDefinition({
		fields: {
			from: {
				label: t('zapis.form.step2.dateFrom.label'),
				isRequired: true,
				isDisabled,
			},
			to: {
				label: t('zapis.form.step2.dateTo.label'),
				isDisabled,
			},
		},
	});

	const fileUpload = useFileUploadDefinition({
		name: 'fileUpload',
		isExpanded: true,
		label: t('zapis.form.step2.fileUpload.label'),
	});

	const form = useFormBuilder<FormStep2FormData, FormStep2Fields>([
		PPPSelect,
		missingPPP,
		missingPPPOvm,
		reasonForAddingMissingPPP,
		pppOvmAutocomplete,
		isOVMDefunct,
		defunctOVMName,
		defunctOVMStreet,
		defunctOVMCity,
		defunctOVMPostalCode,
		defunctOVMCityPart,
		defunctOVMDescriptiveNumber,
		defunctOVMEvidenceNumber,
		defunctOVMOrientationNumber,
		number,
		issueDate,
		datumOd,
		datumDo,
		fileUpload,
	]);

	const isOVMDefunctValue = form.formMethods.watch('isOVMDefunct');

	return {
		...form,
		fields: {
			...form.fields,
			pppOvmAutocomplete: {
				...form.fields.pppOvmAutocomplete,
				field: {
					...form.fields.pppOvmAutocomplete.field,
					isDisabled:
						form.fields.pppOvmAutocomplete.field.isDisabled ||
						!!isOVMDefunctValue,
				},
			},
		},
	};
};
